"use client";

import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";

export type LoginModalSource = "auth-guard" | "expired" | "logout" | "im" | "manual";

interface LoginModalContextValue {
    open: boolean;
    source: LoginModalSource;
    openModal: ( source?: LoginModalSource ) => void;
    closeModal: () => void;
}

const LoginModalContext = createContext<LoginModalContextValue | null>( null );
let globalLoginModalOpener: (( source?: LoginModalSource ) => void) | null = null;

export function LoginModalProvider( { children }: { children: ReactNode } ) {
    const [ open, setOpen ] = useState( false );
    const [ source, setSource ] = useState<LoginModalSource>( "manual" );

    const value = useMemo<LoginModalContextValue>( () => ( {
        open,
        source,
        openModal: ( nextSource = "manual" ) => {
            setSource( nextSource );
            setOpen( true );
        },
        closeModal: () => setOpen( false ),
    } ), [ open, source ] );

    useEffect( () => {
        globalLoginModalOpener = value.openModal;
        return () => {
            if ( globalLoginModalOpener === value.openModal ) {
                globalLoginModalOpener = null;
            }
        };
    }, [ value ] );

    return <LoginModalContext.Provider value={value}>{children}</LoginModalContext.Provider>;
}

export function openLoginModal( source: LoginModalSource = "manual" ) {
    globalLoginModalOpener?.( source );
}

export function useLoginModal() {
    const context = useContext( LoginModalContext );

    if ( !context ) {
        throw new Error( "useLoginModal must be used within LoginModalProvider" );
    }

    return context;
}
